'use client';

import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Users, TrendingUp, Calendar, BarChart3 } from 'lucide-react';
import { useAuthGuard } from '@/hooks/useAuthGuard';
import { useTranslation } from '@/hooks/useTranslation';
import { meetingAttendanceApi } from '@/services/api/meeting-attendance';

interface ReportData {
  summary: {
    totalAttendances: number;
    presentCount: number;
    lateCount: number;
    absentCount: number;
    attendanceRate: number;
  };
  departmentStats: {
    department: string;
    total: number;
    present: number;
    attendanceRate: number;
  }[];
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

export default function TeamsReportsPage() {
  const { isReady, user } = useAuthGuard();
  const { t } = useTranslation();
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const userRoles = (user as { roles?: string[] })?.roles || [];
  const canViewReport = userRoles.some((role) =>
    ['Administrator', 'MeetingManager', 'Leader'].includes(role),
  );

  useEffect(() => {
    const teams = (typeof window !== 'undefined' ? (window as any).microsoftTeams : null);
    if (teams?.app) {
      teams.app.initialize();
    }
  }, []);

  useEffect(() => {
    if (!isReady) return;

    setLoading(true);
    meetingAttendanceApi.getAttendanceReport()
      .then((data) => setReportData(data))
      .catch(() => {
        setReportData(null);
      })
      .finally(() => setLoading(false));
  }, [isReady]);

  if (!isReady || loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t.meetingAttendance.teamsReports.loading}</p>
        </div>
      </div>
    );
  }

  if (!canViewReport) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <BarChart3 className="h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">{t.meetingAttendance.teamsReports.noAccessTitle}</h3>
            <p className="text-gray-500 text-center">
              {t.meetingAttendance.teamsReports.noAccessDesc}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!reportData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <BarChart3 className="h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">{t.meetingAttendance.teamsReports.noDataTitle}</h3>
            <p className="text-gray-500 text-center">{t.meetingAttendance.teamsReports.noDataDesc}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{t.meetingAttendance.teamsReports.title}</h1>
          <p className="text-gray-600">{t.meetingAttendance.teamsReports.subtitle}</p>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Users className="h-6 w-6 text-blue-600" />
                </div>
                <div className="ml-3">
                  <div className="text-lg font-bold text-gray-900">{reportData.summary.totalAttendances}</div>
                  <div className="text-xs text-gray-500">{t.meetingAttendance.teamsReports.totalAttendanceRecords}</div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <TrendingUp className="h-6 w-6 text-green-600" />
                </div>
                <div className="ml-3">
                  <div className="text-lg font-bold text-gray-900">{reportData.summary.presentCount}</div>
                  <div className="text-xs text-gray-500">{t.meetingAttendance.teamsReports.checkedIn}</div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Calendar className="h-6 w-6 text-yellow-600" />
                </div>
                <div className="ml-3">
                  <div className="text-lg font-bold text-gray-900">{reportData.summary.lateCount}</div>
                  <div className="text-xs text-gray-500">{t.meetingAttendance.teamsReports.late}</div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <BarChart3 className="h-6 w-6 text-purple-600" />
                </div>
                <div className="ml-3">
                  <div className="text-lg font-bold text-gray-900">{reportData.summary.attendanceRate}%</div>
                  <div className="text-xs text-gray-500">{t.meetingAttendance.teamsReports.attendanceRate}</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">{t.meetingAttendance.teamsReports.departmentStatsTitle}</CardTitle>
              <CardDescription>{t.meetingAttendance.teamsReports.departmentStatsDesc}</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={reportData.departmentStats.slice(0, 5)}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="department" fontSize={12} tick={{ fontSize: 12 }} />
                  <YAxis fontSize={12} />
                  <Tooltip />
                  <Bar dataKey="present" fill="#10B981" name={t.meetingAttendance.teamsReports.checkedIn} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">{t.meetingAttendance.teamsReports.statusDistributionTitle}</CardTitle>
              <CardDescription>{t.meetingAttendance.teamsReports.statusDistributionDesc}</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={[
                      { name: t.meetingAttendance.teamsReports.checkedIn, value: reportData.summary.presentCount },
                      { name: t.meetingAttendance.teamsReports.late, value: reportData.summary.lateCount },
                      { name: t.meetingAttendance.teamsReports.absent, value: reportData.summary.absentCount },
                    ]}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${((percent || 0) * 100).toFixed(0)}%`}
                    outerRadius={60}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {[0, 1, 2].map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{t.meetingAttendance.teamsReports.departmentDetailsTitle}</CardTitle>
            <CardDescription>{t.meetingAttendance.teamsReports.departmentDetailsDesc}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {reportData.departmentStats.slice(0, 8).map((dept, index) => (
                <div key={dept.department} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-medium">
                      {index + 1}
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900">{dept.department}</h4>
                      <div className="text-sm text-gray-500">
                        {t.meetingAttendance.teamsReports.departmentCheckinCount
                          .replace('{attended}', String(dept.present))
                          .replace('{total}', String(dept.total))}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-gray-900">{dept.attendanceRate}%</div>
                    <div className="text-xs text-gray-500">{t.meetingAttendance.teamsReports.attendanceRate}</div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
